import { useState, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Search, 
  BookOpen, 
  Play, 
  ChevronRight,
  ShoppingBag,
  Package,
  DollarSign,
  Building2,
  Layers,
  Receipt,
  Image,
  BarChart3,
  Calculator,
  FileText,
  Sparkles,
  Heart,
  ArrowLeft,
  Lightbulb,
  CheckCircle2,
  Users,
  Store
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getAllTutorials, searchTutorials, getTutorialById, getTutorialCategories } from '../lib/tutorialData';
import { InteractiveTutorial } from '../components/InteractiveTutorial';

// Icon mapping
const iconMap = {
  ShoppingBag: ShoppingBag,
  Package: Package,
  DollarSign: DollarSign,
  Building2: Building2,
  Layers: Layers,
  Receipt: Receipt,
  Image: Image,
  BarChart3: BarChart3,
  Calculator: Calculator,
  FileText: FileText,
  Sparkles: Sparkles,
  Heart: Heart,
  Users: Users,
  Store: Store
};

// Route mapping for tutorials
const routeMap = {
  products: '/products',
  materials: '/materials',
  sales: '/sales',
  boutiques: '/boutiques',
  inventory: '/inventory',
  expenses: '/expenses',
  media: '/media',
  analytics: '/analytics',
  whatif: '/what-if',
  applications: '/applications',
  eventscout: '/ai-discover',
  events: '/events',
  vendorCommunity: '/community',
  editProfile: '/profile/edit'
};

// Tutorial Card Component
const TutorialCard = ({ tutorial }) => {
  const navigate = useNavigate();
  const IconComponent = iconMap[tutorial.icon] || BookOpen;
  
  const handleStartTutorial = () => {
    // Navigate to the page with a query param to trigger the tutorial
    navigate(`${routeMap[tutorial.id]}?tutorial=${tutorial.id}`);
  };
  
  return (
    <div 
      className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#1E3A5F] p-6 transition-all hover:shadow-lg group"
      data-testid={`tutorial-card-${tutorial.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-[#1E3A5F]/10 text-[#1E3A5F] group-hover:bg-[#1E3A5F] group-hover:text-white transition-colors">
          <IconComponent className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-[#000501] mb-1">
            How to Get Started with {tutorial.title}
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            {tutorial.description}
          </p>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={handleStartTutorial}
              className="bg-[#1E3A5F] hover:bg-[#152a47] text-white"
              data-testid={`start-tutorial-${tutorial.id}`}
            >
              <Play className="w-4 h-4 mr-1" />
              Start Tutorial
            </Button>
            <Link to={`/help/${tutorial.id}`}>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-[#1E3A5F]"
                data-testid={`learn-more-${tutorial.id}`}
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Full Guide
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Help Center Main Page
export const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const allTutorials = getAllTutorials();
  const categories = useMemo(() => ['All', ...getTutorialCategories()], []);

  const filteredTutorials = useMemo(() => {
    // Search takes precedence — if the user is searching, ignore the chip filter
    // so they never see "No results" because of a leftover category selection.
    const base = searchQuery.trim()
      ? searchTutorials(searchQuery)
      : allTutorials;
    if (activeCategory === 'All') return base;
    return base.filter((t) => (t.category || 'Other') === activeCategory);
  }, [searchQuery, activeCategory, allTutorials]);

  // Per-category counts for the chip badges (respects current search query)
  const countsByCategory = useMemo(() => {
    const source = searchQuery.trim()
      ? searchTutorials(searchQuery)
      : allTutorials;
    const counts = { All: source.length };
    for (const t of source) {
      const c = t.category || 'Other';
      counts[c] = (counts[c] || 0) + 1;
    }
    return counts;
  }, [searchQuery, allTutorials]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#f8faf9]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#152a47] text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-10 h-10" />
            <h1 className="text-3xl font-bold">Help Center</h1>
          </div>
          <p className="text-white/90 text-lg mb-6 max-w-2xl">
            Learn how to use AMSCompass with interactive tutorials and comprehensive guides. 
            Get started quickly and master every feature.
          </p>
          
          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search tutorials... (e.g., 'pricing', 'inventory', 'event')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg bg-white border-0 shadow-lg rounded-xl text-[#000501]"
              data-testid="tutorial-search-input"
            />
          </div>
        </div>
      </div>
      
      {/* Tutorial Grid */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Category chips — one-tap topic filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="help-category-chips">
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            const count = countsByCategory[cat] || 0;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition ${
                  isActive
                    ? 'bg-[#1E3A5F] text-white border-[#1E3A5F] shadow-sm'
                    : 'bg-white text-[#1E3A5F] border-gray-200 hover:border-[#1E3A5F]'
                }`}
                data-testid={`help-chip-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {cat}
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1 rounded-full text-[11px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-[#71717A]'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {searchQuery && (
          <p className="text-gray-600 mb-6">
            {filteredTutorials.length} tutorial{filteredTutorials.length !== 1 ? 's' : ''} found for "{searchQuery}"
            {activeCategory !== 'All' && <> in <span className="font-semibold text-[#1E3A5F]">{activeCategory}</span></>}
          </p>
        )}
        
        {filteredTutorials.length === 0 ? (
          <div className="text-center py-12" data-testid="help-empty-state">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No tutorials found</h3>
            <p className="text-gray-500">
              {activeCategory !== 'All'
                ? <>No matches in <span className="font-semibold">{activeCategory}</span>. <button className="underline text-[#1E3A5F]" onClick={() => setActiveCategory('All')}>Show all categories</button></>
                : 'Try a different search term'}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {filteredTutorials.map((tutorial) => (
              <TutorialCard 
                key={tutorial.id} 
                tutorial={tutorial} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Tutorial Detail Page (Full Guide)
export const TutorialDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  
  const tutorial = getTutorialById(id);
  
  if (!tutorial) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Tutorial Not Found</h2>
          <p className="text-gray-600 mb-4">The tutorial you're looking for doesn't exist.</p>
          <Link to="/help">
            <Button className="bg-[#1E3A5F] hover:bg-[#152a47]">
              Back to Help Center
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const IconComponent = iconMap[tutorial.icon] || BookOpen;
  
  const handleStartTutorial = () => {
    navigate(routeMap[tutorial.id]);
    setTimeout(() => {
      setActiveTutorial(tutorial);
      setIsTutorialOpen(true);
    }, 500);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#f8faf9]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#152a47] text-white">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <Link 
            to="/help" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            data-testid="back-to-help"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Center
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 rounded-xl bg-white/20">
              <IconComponent className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2">
                How to Get Started with {tutorial.title}
              </h1>
              <p className="text-white/90 text-lg">
                {tutorial.description}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Quick Start Button */}
        <div className="bg-[#1E3A5F]/5 border-2 border-[#1E3A5F]/20 rounded-xl p-6 mb-10 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#000501] mb-1">Ready to learn by doing?</h3>
            <p className="text-gray-600 text-sm">Take an interactive walkthrough of the {tutorial.title} module.</p>
          </div>
          <Button 
            onClick={handleStartTutorial}
            className="bg-[#1E3A5F] hover:bg-[#152a47] text-white"
            data-testid="start-interactive-tutorial"
          >
            <Play className="w-4 h-4 mr-2" />
            Start Interactive Tutorial
          </Button>
        </div>
        
        {/* Sections */}
        <div className="space-y-8">
          {tutorial.fullGuide.sections.map((section, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#000501] mb-2">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Tips Section */}
        <div className="mt-10 bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-6 h-6 text-amber-600" />
            <h3 className="font-semibold text-lg text-amber-900">Pro Tips</h3>
          </div>
          <ul className="space-y-3">
            {tutorial.fullGuide.tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-amber-900">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Navigation to Module */}
        <div className="mt-10 text-center">
          <p className="text-gray-600 mb-4">Ready to get started?</p>
          <Link to={routeMap[tutorial.id]}>
            <Button 
              size="lg"
              className="bg-[#1E3A5F] hover:bg-[#152a47] text-white"
              data-testid="go-to-module"
            >
              Go to {tutorial.title}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Interactive Tutorial */}
      <InteractiveTutorial
        tutorial={activeTutorial}
        isOpen={isTutorialOpen}
        onClose={() => setIsTutorialOpen(false)}
        onLearnMore={() => {}}
      />
    </div>
  );
};

export default HelpCenter;
